import { create } from 'zustand';
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware';
import { VideoFeaturesStore, VideoFeatures, SpeedTest } from './types';
import { initialState } from './initialState';
import { migrateAutoplayABTest } from '@/utils/migrateAutoplayABTest';
import { emergencyStorageCleanup } from '@/utils/storageCleanup';

// Run migration on import
migrateAutoplayABTest();

// Custom storage with QuotaExceededError handling
const safeStorage: StateStorage = {
  getItem: (name: string): string | null => {
    try {
      return localStorage.getItem(name);
    } catch (error) {
      console.error('Failed to get from localStorage:', error);
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      localStorage.setItem(name, value);
    } catch (error) {
      if (error instanceof DOMException && 
          (error.name === 'QuotaExceededError' || error.code === 22)) {
        console.warn('⚠️ Storage quota exceeded, cleaning up...');
        
        // Tentar limpeza de emergência
        const cleaned = emergencyStorageCleanup();
        
        if (cleaned > 0) {
          // Tentar novamente após limpeza
          try {
            localStorage.setItem(name, value);
            console.log('✅ Save successful after cleanup');
            return;
          } catch (retryError) {
            console.error('❌ Still cannot save after cleanup');
          }
        }
        
        // Se ainda falhar, mostrar erro no console
        console.error('❌ Storage quota exceeded. Please remove some images or clear cache.');
      } else {
        throw error;
      }
    }
  },
  removeItem: (name: string): void => {
    try {
      localStorage.removeItem(name);
    } catch (error) {
      console.error('Failed to remove from localStorage:', error);
    }
  }
};

// Clean up any leftover 'video-features-temp-*' keys on initialization.
// These were written by an older autosave path that nothing ever read; they
// could accumulate up to ~5 MB (especially with miniHooks containing base64
// images) and exhaust the localStorage quota.
try {
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('video-features-temp-')) {
      localStorage.removeItem(key);
      console.log('🧹 Cleaned up legacy temp key:', key);
    }
  });
} catch (error) {
  console.error('Failed to cleanup localStorage on init:', error);
}

// Clean up finished A/B tests on initialization
function cleanupFinishedTests(state: VideoFeaturesStore): VideoFeaturesStore {
  if (state.features?.autoplayOverlay?.abTestConfig?.status === 'finished') {
    console.log('🧹 Limpando teste A/B finalizado após F5');
    
    return {
      ...state,
      features: {
        ...state.features,
        autoplayOverlay: {
          ...state.features.autoplayOverlay,
          abTestConfig: {
            enabled: false,
            variations: [],
            selectedTemplates: [],
            trafficDistribution: {},
            variantTrafficDistribution: {},
            status: 'draft',
            isRealTimeEnabled: false,
            lastUpdated: ''
          }
        }
      }
    };
  }
  
  return state;
}

export const useVideoFeaturesStore = create(
  persist<VideoFeaturesStore>(
    (set, get) => ({
      features: { ...initialState } as VideoFeatures,
      currentFeature: null,
      hasUnsavedChanges: false,
      tempChanges: {},
      tempFiles: {
        logoFile: null,
        pauseThumbnailFile: null,
        initialThumbnailFile: null,
        endThumbnailFile: null,
        autoplayCustomImageFile: null,
      },
      setTempFile: (key, file) => set((state) => ({
        tempFiles: {
          ...state.tempFiles,
          [key]: file
        }
      })),
      clearTempFile: (key) => set((state) => ({
        tempFiles: {
          ...state.tempFiles,
          [key]: null
        }
      })),
      clearAllTempFiles: () => set(() => ({
        tempFiles: {
          logoFile: null,
          pauseThumbnailFile: null,
          initialThumbnailFile: null,
          endThumbnailFile: null,
          autoplayCustomImageFile: null,
        }
      })),
      setFeature: (key, value) => set((state) => {
        try {
          // Validação específica para headline
          if (key === 'headline' && value) {
            // Garantir que headline tenha pelo menos as propriedades essenciais
            const validatedHeadline = {
              enabled: false,
              headline: { text: '', fontSize: 'medium', fontFamily: 'Arial', fontWeight: 'normal', color: '#000000' },
              subheadline: { text: '', fontSize: 'small', fontFamily: 'Arial', fontWeight: 'normal', color: '#666666' },
              timing: { startTime: 0, endTime: 0, duration: 5 },
              position: { vertical: 'center', horizontal: 'center' },
              background: { type: 'none', color: '#ffffff', opacity: 0.8 },
              animation: { type: 'none', duration: 1000, delay: 0 },
              htmlMode: { enabled: false, code: '' },
              imageMode: { enabled: false, desktopImage: '', mobileImage: '' },
              abTestConfig: { enabled: false, variations: [], trafficSplit: 50, winningVariation: null },
              ...(value && typeof value === 'object' && !Array.isArray(value) ? value : {})
            };

            return {
              features: {
                ...state.features,
                [key]: validatedHeadline
              },
              hasUnsavedChanges: true
            };
          }

          // Persistence is handled by the Zustand `persist` middleware below
          // (key: 'video-features-storage'). The previous duplicate write to
          // 'video-features-temp-autosave' was never read anywhere and could
          // grow stale (e.g., miniHooks with base64 images), eating the entire
          // localStorage quota.
          return {
            features: {
              ...state.features,
              [key]: value
            },
            hasUnsavedChanges: true
          };
        } catch (error) {
          console.error('🏪 Store: Error setting feature:', error);
          return state; // Return previous state on error
        }
      }),
      setCurrentFeature: (featureId) => set(() => ({
        currentFeature: featureId
      })),
      setHasUnsavedChanges: (value) => set(() => ({
        hasUnsavedChanges: value
      })),
      resetFeatures: () => set(() => ({ 
        features: { ...initialState } as VideoFeatures,
        hasUnsavedChanges: false
      })),
      addSpeedTest: (test: SpeedTest) => set((state) => {
        // Initialize speedTests array if it doesn't exist
        const speedTests = state.features.speedTests || [];
        
        // Filter out any existing test with the same videoId
        const filteredTests = speedTests.filter(
          t => {
            // Check both videoId formats (old and new)
            if (test.videoId && t.videoId) {
              return t.videoId !== test.videoId;
            }
            // For old format with velocidade
            if (test.velocidade !== undefined && t.velocidade !== undefined) {
              return t.velocidade !== test.velocidade;
            }
            return true;
          }
        );
        
        return {
          features: {
            ...state.features,
            speedTests: [...filteredTests, test]
          },
          hasUnsavedChanges: true
        };
      }),
      saveFeatures: async () => {
        // In a real application, this would save to an API
        return new Promise<void>((resolve) => {
          setTimeout(() => {
            // Limpar autosave temporário quando salvar
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
              if (key.startsWith('video-features-temp-')) {
                localStorage.removeItem(key);
              }
            });
            
            set({ hasUnsavedChanges: false });
            resolve();
          }, 500);
        });
      },
      loadVideoFeatures: async (videoId) => {
        // In a real application, this would load from an API
        return new Promise<void>((resolve) => {
          setTimeout(() => {
            set((state) => {
              const loadedFeatures = { ...state.features, videoId };
              
              // Migration: Convert old icon types to new ones
              if (loadedFeatures.playIcon === 'round') {
                loadedFeatures.playIcon = 'neo-glossy';
              }
              if (loadedFeatures.playIcon === 'minimal') {
                loadedFeatures.playIcon = 'gradient-pulse';
              }
              
              // Migration: Remove old theme system, overlayBlur, and add fullScreen for Final Countdown.
              // Também descarta `titleBackgroundColor`/`descriptionBackgroundColor` (campos que
              // saíram do tipo no realinhamento com o design de referência) e preenche os que
              // entraram (`linkOpenMode`, `stylePreset`, `redirectMode`) — sem isso uma config
              // salva antes reidrata sem esses campos e o painel abre com os selects vazios.
              if (loadedFeatures.finalCountdown) {
                const {
                  theme,
                  overlayBlur,
                  titleBackgroundColor,
                  descriptionBackgroundColor,
                  ...rest
                } = loadedFeatures.finalCountdown as any;

                loadedFeatures.finalCountdown = {
                  ...rest,
                  fullScreen: rest.fullScreen ?? false,
                  overlayColor: rest.overlayColor || '#000000',
                  overlayOpacity: rest.overlayOpacity ?? 70,
                  cardOpacity: rest.cardOpacity ?? 100,
                  cardBorderRadius: rest.cardBorderRadius ?? 12,
                  circleColor: rest.circleColor || '#f3f4f6',
                  circleTextColor: rest.circleTextColor || '#000000',
                  circleSize: rest.circleSize || 'medium',
                  titleColor: rest.titleColor || '#000000',
                  descriptionColor: rest.descriptionColor || '#666666',
                  linkOpenMode: rest.linkOpenMode || 'sameTab',
                  stylePreset: rest.stylePreset || 'host',
                  redirectMode: rest.redirectMode || 'redirect'
                };
              }
              
              return {
                features: loadedFeatures,
                hasUnsavedChanges: false
              };
            });
            resolve();
          }, 500);
        });
      }
    }),
    {
      name: 'video-features-storage',
      storage: createJSONStorage(() => safeStorage),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Limpa testes finalizados após carregar do localStorage
          const cleanedState = cleanupFinishedTests(state);
          if (cleanedState !== state) {
            // Se houve mudança, atualiza o store
            state.features = cleanedState.features;
          }
          // blob: URLs não sobrevivem a refresh (são descartadas com a aba).
          // Se houver alguma persistida, removemos para evitar src quebrado.
          const customImg = state.features?.autoplayOverlay?.customImage;
          if (typeof customImg === 'string' && customImg.startsWith('blob:')) {
            state.features.autoplayOverlay = {
              ...state.features.autoplayOverlay,
              customImage: undefined,
            };
          }
        }
      }
    }
  )
);
